import { star } from '../patterns/star.js';
import { beads } from '../beads.js';
import { settings } from '../settings.js';

export const mirror = {};

mirror.start = bead => {
    if (settings.pattern.startMirror) {
        settings.pattern.startMirror();
        return;
    }

    beads.mirror.forEach(mirror => {
        mirror.setX(bead.x);
        mirror.setY(bead.y);
    });

    if (!beads.mirror.active || (beads.mirror.orientation === 'none')) {
        beads.mirror.orientation = 'vertical';
        beads.mirror[0].setAlpha(1);
        beads.mirror[1].setAlpha(0);
        beads.mirror.active = true;
    }
};

mirror.findMirrorBead = bead => {
    if (settings.pattern.findMirrorBead) {
        return settings.pattern.findMirrorBead(bead);
    }

    let result = [];
    let mx = beads.mirror[0].x;
    let my = beads.mirror[0].y;
    if (beads.mirror.orientation == 'vertical') {
        if (bead.x !== mx) {
            let xDist = (mx - bead.x);
            result.push(beads.arr.find(
                element => element.x == (mx + xDist) && element.y == bead.y
            ));
        }
    } else if (beads.mirror.orientation == 'horizontal') {
        if (bead.y !== my) {
            let check = +(my + my - bead.y).toFixed(2);
            result.push(beads.arr.find(
                element => element.y == check && element.x == bead.x
            ));
        }
    } else if (beads.mirror.orientation == 'both') {
        let check = +(my + my - bead.y).toFixed(2);
        let xDist = (mx - bead.x);
        result.push(
            beads.arr.find(element => element.y == check && element.x == bead.x),
            beads.arr.find(element => element.x == (mx + xDist) && element.y == bead.y),
            beads.arr.find(element => element.y == check && element.x == (mx + xDist))
        );
    }
    return result;
};

const findStarShapeBead = bead => { // rotational symmetry for star view
    const result = [];
    if (beads.mirror.orientation !== 'none') {
        beads.arr.forEach(_bead => {
            if (!_bead.star) return;
            if ((_bead.star.side % 2) === (bead.star.side % 2)) {
                if (_bead.star.row === bead.star.row && (_bead.star.beadNumber === bead.star.beadNumber)) {
                    result.push(_bead);
                }
            }
        });
    }

    return result;
};

const isOppositeSide = (s1, s2) => {
    if (s1 === 1 && s2 === 4) return true;
    if (s1 === 2 && s2 === 3) return true;
    if (s1 === 3 && s2 === 2) return true;
    if (s1 === 4 && s2 === 1) return true;
    if (s1 === s2) return true;
    return false;
};

const isAdjacentSide = (s1, s2) => {
    if (s1 === 1 && s2 === 2) return true;
    if (s1 === 2 && s2 === 1) return true;
    if (s1 === 3 && s2 === 4) return true;
    if (s1 === 4 && s2 === 3) return true;
    return false;
};

const isReflectedBead = (bead, _bead) => {
    const row = bead.star.row;
    const num = bead.star.beadNumber;
    const reflectedNum = row - num + 1;

    return _bead.star.beadNumber === reflectedNum;
};

mirror.findStarMirrorBead = bead => {
    if (['F', 'B'].some(element => element === star.currentView)) {
        return findStarShapeBead(bead);
    }
    const result = [];
    if (beads.mirror.orientation === 'horizontal') { // rotational symmetry
        beads.arr.forEach(_bead => {
            if (!_bead.star) return;
            if (_bead.star.square !== bead.star.square) return;
            if (_bead.star.row !== bead.star.row) return;
            if (isAdjacentSide(_bead.star.side, bead.star.side)) {
                if (_bead.star.beadNumber === bead.star.beadNumber) {
                    result.push(_bead);
                }
            } else {
                if (_bead.star.side === bead.star.side) return;
                if (isReflectedBead(bead, _bead)) {
                    result.push(_bead);
                }
            }
        });
    } else if (beads.mirror.orientation === 'vertical') { // reflection symmetry

        const getCalc = b => Math.abs(b.star.beadNumber - +((b.star.row + 1) / 2).toFixed(2));

        beads.arr.forEach(_bead => {
            if (!_bead.star) return;
            if (_bead.star.square !== bead.star.square) return;
            if (isOppositeSide(_bead.star.side, bead.star.side)) {
                if (_bead.star.row === bead.star.row) {
                    if (getCalc(bead) === getCalc(_bead)) {
                        result.push(_bead);
                    }
                }
            }
        });
    }

    return result;
};

mirror.findSquareMirrorBead = bead => {

    const result = [];
    if (beads.mirror.orientation === 'horizontal') { // rotational symmetry
        beads.arr.forEach(_bead => {
            if (_bead.star !== undefined) {
                if (_bead.star.row === bead.star.row && (_bead.star.beadNumber === bead.star.beadNumber)) {
                    result.push(_bead);
                }
            }
        });
    } else if (beads.mirror.orientation === 'vertical') { // reflection symmetry
        beads.arr.forEach(_bead => {
            if (_bead.star !== undefined) {
                if ((_bead.star.side % 2) === (bead.star.side % 2)) {
                    if (_bead.star.row === bead.star.row) {
                        const getCalc = b => Math.abs(b.star.beadNumber - +((b.star.row + 1) / 2).toFixed(2));
                        if (getCalc(bead) === getCalc(_bead)) {
                            result.push(_bead);
                        }
                    }
                }
            }
        });
    }

    return result;
};

mirror.findMedallionMirrorBead = bead => {
    let result = [];

    let row = bead.circleAddress.r;
    let vertical = (row * 8) + ((-bead.circleAddress.i) % (row * 8));
    let horizontal = (((row * 4) - bead.circleAddress.i) % (row * 8));
    if (horizontal < 0) horizontal += (row * 8);

    let addFn = address => {
        result.push(
            beads.arr.find(element => (element.circleAddress.r == bead.circleAddress.r) && (element.circleAddress.i == address))
        );
    };

    [
        {orientation: 'vertical', mirrorBead: vertical},
        {orientation: 'horizontal', mirrorBead: horizontal},
        {orientation: 'round2', rotationBead: [4]},
        {orientation: 'round4', rotationBead: [2, 4, 6]},
        {orientation: 'round8', rotationBead: [1, 2, 3, 4, 5, 6, 7]},
    ].forEach(element => {
        if (beads.mirror.orientation == element.orientation) {
            if (element.mirrorBead !== undefined) addFn(element.mirrorBead);
            else element.rotationBead.forEach(num => {
                addFn((bead.circleAddress.i + (row * num)) % (row * 8))
            });
        }
    });

    return result;
};

mirror.rotateMedallionMirror = () => {

    if (beads.mirror.orientation == 'none') {
        beads.mirror.orientation = 'vertical';
        beads.mirror[0].setAlpha(1);
    } else if (beads.mirror.orientation == 'vertical') {
        beads.mirror.orientation = 'horizontal';
        beads.mirror[0].setAlpha(0);
        beads.mirror[1].setAlpha(1);
    } else if (beads.mirror.orientation == 'horizontal') {
        beads.mirror.orientation = 'round2';
        beads.mirror[1].setFillStyle(0x00ffff);
    } else if (beads.mirror.orientation == 'round2') {
        beads.mirror.orientation = 'round4';
        beads.mirror[0].setAlpha(1);
    } else if (beads.mirror.orientation == 'round4') {
        beads.mirror.orientation = 'round8';
        beads.mirror[0].setFillStyle(0x00ffff);
    } else if (beads.mirror.orientation == 'round8') {
        beads.mirror.orientation = 'none';
        beads.mirror[0].setFillStyle(0xff0000);
        beads.mirror[1].setFillStyle(0xff0000);
        beads.mirror[0].setAlpha(0);
        beads.mirror[1].setAlpha(0);
    }
};

mirror.rotateStarMirror = () => {
    if (beads.mirror.orientation === 'none') {
        beads.mirror.orientation = 'vertical';
        beads.mirror[0].setAngle(0).setAlpha(1);
        beads.mirror[1].setAngle(0).setAlpha(0);
    } else {
        beads.mirror.orientation = 'none';
        beads.mirror[0].setAngle(0).setAlpha(0);
        beads.mirror[1].setAngle(0).setAlpha(0);
    }
};

mirror.rotateSquareMirror = () => {

    beads.mirror.forEach(mirror => {
        mirror.setX(star.mirrorPoint.x);
        mirror.setY(star.mirrorPoint.y);
    });

    if (settings.pattern.name === 'star') {
        if (['F', 'B'].some(element => element === star.currentView)) {
            mirror.rotateStarMirror();
            return;
        }
    }

    if (beads.mirror.orientation === 'none') {
        beads.mirror.orientation = 'vertical';
        beads.mirror[0].setAlpha(1);
        beads.mirror[1].setAlpha(1);
    } else if (beads.mirror.orientation === 'vertical') {
        beads.mirror.orientation = 'horizontal';
        beads.mirror[0].setAngle(45);
        beads.mirror[1].setAngle(225);
    } else if (beads.mirror.orientation === 'horizontal') {
        beads.mirror.orientation = 'none';
        beads.mirror[0].setAngle(0).setAlpha(0);
        beads.mirror[1].setAngle(0).setAlpha(0);
    }
};

mirror.remove = () => {
    beads.mirror.active = false;
    beads.mirror.forEach((mirror) => mirror.setAlpha(0));
    beads.mirror.orientation = 'vertical';
};

mirror.rotate = () => {
    if (settings.pattern.rotateMirror) {
        settings.pattern.rotateMirror();
        return;
    }
    if (settings.pattern.name === 'triangle') {
        mirror.rotateTriangleMirror();
        return;
    }
    if (beads.mirror.orientation === 'vertical') {
        beads.mirror.orientation = 'horizontal';
        beads.mirror[0].setAlpha(0);
        beads.mirror[1].setAlpha(1);
    } else if (beads.mirror.orientation === 'horizontal') {
        beads.mirror.orientation = 'both';
        beads.mirror[0].setAlpha(1);
    } else if (beads.mirror.orientation === 'both') {
        beads.mirror.orientation = 'none';
        beads.mirror[0].setAlpha(0);
        beads.mirror[1].setAlpha(0);
    } else if (beads.mirror.orientation === 'none') {
        beads.mirror.orientation = 'vertical';
        beads.mirror[0].setAlpha(1);
    }
};